import { Component, inject, QueryList, ViewChildren, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CartRulesApiService, numberValidator, AdminProductsApiService, UserApiService, GetApiRequest, LazyDropdownComponent, LazyDropdownItem } from '@core';
import {
  ButtonClickEvent,
  CacButtonComponent,
  CacFieldComponent,
  CacIconComponent,
} from '@controladad/ng-base';
import { formControl, formGroup, Validators, FormControlExtended } from '@al00x/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CartRulesEntity, ProductDropdownItem, GiftTier, UserEntity } from '@models';
import { take } from 'rxjs';
import { BehaviorSubject } from 'rxjs';

declare const snackbar$: any;

interface GiftTierForm {
  minimumCartPrice: FormControlExtended<number | undefined>;
  selectedGifts: BehaviorSubject<ProductDropdownItem[]>;
}

@Component({
  selector: 'lib-cart-rules',
  imports: [CommonModule, CacFieldComponent, CacButtonComponent, CacIconComponent, LazyDropdownComponent],
  templateUrl: './cart-rules.component.html',
  styleUrl: './cart-rules.component.scss',
})
export class CartRulesComponent {
  private readonly cartRulesApi = inject(CartRulesApiService);
  private readonly productApi = inject(AdminProductsApiService);
  private readonly userApi = inject(UserApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  currentData: CartRulesEntity | undefined = undefined;
  configurationLoaded = false;
  targetedShippingForm = formGroup({ isTargetedFreeShippingEnabled: formControl<boolean>(false) });
  freeShippingCustomers: LazyDropdownItem[] = [];
  freeShippingProducts: LazyDropdownItem[] = [];
  customerApiAdapter = this.userApi.getCustomerAdapter();
  customerItemMapper = (user: UserEntity): LazyDropdownItem => ({
    id: user.id, value: user.id,
    label: user.fullName || user.email || user.phoneNumber || `Customer #${user.id}`,
    subtitle: `#${user.id}`,
  });
  get selectedCustomerIds() { return this.freeShippingCustomers.map(item => item.id); }
  get selectedProductIds() { return this.freeShippingProducts.map(item => item.id); }

  @ViewChildren('giftDropdown') giftDropdowns?: QueryList<LazyDropdownComponent>;

  // Form controls
  generalForm = formGroup({
    minimumCheckoutPrice: formControl<number>(undefined, Validators.required),
  });

  giftForm = formGroup({
    isGiftAllowed: formControl<boolean>(false, Validators.required),
  });

  freeShippingForm = formGroup({
    isFreeShippingEnabled: formControl<boolean>(false, Validators.required),
    minimumCartPriceForFreeShipping: formControl<number | undefined>(undefined, [
      numberValidator(),
      Validators.required
    ]),
  });

  // Gift tiers management
  giftTiers$ = new BehaviorSubject<GiftTierForm[]>([]);

  // Product dropdown configuration for lazy dropdown
  productApiAdapter = this.productApi.getProductNamesAdapter();

  productItemMapper = (product: ProductDropdownItem): LazyDropdownItem => ({
    id: product.id,
    label: product.name.english || product.name.arabic || 'Unnamed Product',
    value: product.id,
    image: product.productFirstImage,
    subtitle: product.name.arabic && product.name.english !== product.name.arabic ? product.name.arabic : undefined,
    // Pass through the full product for later use
    sourceData: product,
  });

  constructor() {
    // Watch for gift allowed changes to show/hide fields and update validation
    this.giftForm.controls.isGiftAllowed.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((allowed) => {
        if (!allowed) {
          // Clear all tiers when gift is disabled
          this.giftTiers$.next([]);
        } else if (this.giftTiers$.value.length === 0) {
          // Add initial tier when gift is enabled
          this.addGiftTier();
        }
      });

    // Watch for free shipping enabled changes to update validation
    this.freeShippingForm.controls.isFreeShippingEnabled.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((enabled) => {
        if (!enabled) {
          this.freeShippingForm.controls.minimumCartPriceForFreeShipping.setValue(undefined);
          // Remove required validation when free shipping is disabled
          this.freeShippingForm.controls.minimumCartPriceForFreeShipping.setValidators([numberValidator()]);
        } else {
          // Add required validation when free shipping is enabled
          this.freeShippingForm.controls.minimumCartPriceForFreeShipping.setValidators([Validators.required, numberValidator()]);
        }
        this.freeShippingForm.controls.minimumCartPriceForFreeShipping.updateValueAndValidity();
      });

    // Load initial data
    this.cartRulesApi
      .get()
      .pipe(take(1))
      .subscribe((rules) => {
        this.currentData = rules;
        this.targetedShippingForm.patchValue({ isTargetedFreeShippingEnabled: rules.isTargetedFreeShippingEnabled ?? false });
        this.loadFreeShippingSelections(rules);
        this.configurationLoaded = true;
        this.generalForm.patchValue({ minimumCheckoutPrice: rules.minimumCheckoutPrice });
        this.giftForm.patchValue({
          isGiftAllowed: rules.isGiftAllowed || false,
        });
        this.freeShippingForm.patchValue({
          isFreeShippingEnabled: rules.isFreeShippingEnabled || false,
          minimumCartPriceForFreeShipping: rules.minimumCartPriceForFreeShipping,
        });

        // Load gift tiers if they exist
        if (rules.tiers && rules.tiers.length > 0) {
          this.loadGiftTiers(rules.tiers);
        }
        this.cdr.markForCheck();
      });
  }

  private loadFreeShippingSelections(rules: CartRulesEntity) {
    this.freeShippingCustomers = (rules.freeShippingCustomerIds ?? []).map(id => ({ id, value: id, label: `Customer #${id} (unavailable)` }));
    this.freeShippingProducts = (rules.freeShippingProductIds ?? []).map(id => ({ id, value: id, label: `Product #${id} (unavailable)` }));
    if (this.freeShippingCustomers.length) {
      GetApiRequest<UserEntity>('/AdminPanel/GetCustomerList', {
        filters: [{ key: 'CustomerIds', value: this.selectedCustomerIds }],
        pagination: { page: 1, size: this.freeShippingCustomers.length },
      }).pipe(take(1)).subscribe({
        next: users => {
          this.freeShippingCustomers = this.freeShippingCustomers.map(item => {
            const user = users.find(user => user.id === item.id);
            return user ? this.customerItemMapper(user) : item;
          });
          this.cdr.markForCheck();
        },
        error: () => snackbar$.show('Unable to load selected customer names', 'error'),
      });
    }
    if (this.freeShippingProducts.length) {
      this.productApi.getProductDropdownItems(this.selectedProductIds).pipe(take(1)).subscribe({
        next: products => {
          this.freeShippingProducts = this.freeShippingProducts.map(item => {
            const product = products.find(product => product.id === item.id);
            return product ? this.productItemMapper(product) : item;
          });
          this.cdr.markForCheck();
        },
        error: () => snackbar$.show('Unable to load selected product names', 'error'),
      });
    }
  }

  addShippingSelection(scope: 'customers' | 'products', selection: LazyDropdownItem | LazyDropdownItem[] | null) {
    if (!selection) return;
    const items = Array.isArray(selection) ? selection : [selection];
    if (scope === 'customers') this.freeShippingCustomers = [...new Map([...this.freeShippingCustomers, ...items].map(item => [item.id, item])).values()];
    else this.freeShippingProducts = [...new Map([...this.freeShippingProducts, ...items].map(item => [item.id, item])).values()];
  }

  removeShippingSelection(scope: 'customers' | 'products', id: number) {
    if (scope === 'customers') this.freeShippingCustomers = this.freeShippingCustomers.filter(item => item.id !== id);
    else this.freeShippingProducts = this.freeShippingProducts.filter(item => item.id !== id);
  }

  // Load gift tiers from saved data
  private loadGiftTiers(tiers: GiftTier[]): void {
    const tierForms: GiftTierForm[] = [];

    tiers.forEach((tier) => {
      const tierForm: GiftTierForm = {
        minimumCartPrice: formControl<number | undefined>(tier.minimumCartPrice, [
          Validators.required,
          numberValidator()
        ]),
        selectedGifts: new BehaviorSubject<ProductDropdownItem[]>([]),
      };

      tierForms.push(tierForm);

      // Load products for this tier
      if (tier.products && tier.products.length > 0) {
        this.loadProductsForTier(tier.products, tierForm.selectedGifts);
      }
    });

    this.giftTiers$.next(tierForms);
  }

  // Load products for a specific tier
  private loadProductsForTier(productIds: number[], giftsSubject: BehaviorSubject<ProductDropdownItem[]>): void {
    this.productApi.getProductDropdownItems(productIds)
      .pipe(take(1))
      .subscribe({
        next: (products) => {
          const gifts: ProductDropdownItem[] = products.map(product => ({
            ...product,
            label: product.label || product.name.english
          }));
          giftsSubject.next(gifts);
          // Trigger change detection so the template updates
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading products for tier:', error);
          snackbar$.show('Error loading products for tier');
        }
      });
  }

  // Add a new gift tier
  addGiftTier(): void {
    const currentTiers = this.giftTiers$.value;
    const newTier: GiftTierForm = {
      minimumCartPrice: formControl<number | undefined>(undefined, [
        Validators.required,
        numberValidator()
      ]),
      selectedGifts: new BehaviorSubject<ProductDropdownItem[]>([]),
    };

    this.giftTiers$.next([...currentTiers, newTier]);
  }

  // Remove a gift tier
  removeGiftTier(index: number): void {
    const currentTiers = this.giftTiers$.value;
    const updatedTiers = currentTiers.filter((_, i) => i !== index);
    this.giftTiers$.next(updatedTiers);
  }

  // Handle gift selection for a specific tier
  onGiftChange(selectedItem: LazyDropdownItem | LazyDropdownItem[] | null, tierIndex: number): void {
    if (!selectedItem) {
      return;
    }

    const item = Array.isArray(selectedItem) ? selectedItem[0] : selectedItem;
    if (!item) {
      return;
    }

    const currentTiers = this.giftTiers$.value;
    const tier = currentTiers[tierIndex];

    if (!tier) {
      return;
    }

    // Extract the source product data from the item
    const sourceProduct = item['sourceData'] as ProductDropdownItem;

    const newGift: ProductDropdownItem = {
      id: item.id,
      name: sourceProduct?.name || {
        english: item.label,
        arabic: item.subtitle || item.label
      },
      label: item.label,
      productFirstImage: sourceProduct?.productFirstImage,
    };

    const currentGifts = tier.selectedGifts.value;
    tier.selectedGifts.next([...currentGifts, newGift]);
    this.clearGiftDropdown(tierIndex);
  }

  // Get selected gift IDs for a specific tier (for exclusion)
  getSelectedGiftIds(tierIndex: number): number[] {
    const tier = this.giftTiers$.value[tierIndex];
    return tier ? tier.selectedGifts.value.map(gift => gift.id) : [];
  }

  // Remove gift from a specific tier
  removeGift(tierIndex: number, giftId: number): void {
    const currentTiers = this.giftTiers$.value;
    const tier = currentTiers[tierIndex];

    if (!tier) {
      return;
    }

    const currentGifts = tier.selectedGifts.value;
    const updatedGifts = currentGifts.filter(g => g.id !== giftId);
    tier.selectedGifts.next(updatedGifts);
  }

  // Clear gift dropdown selection for a specific tier
  private clearGiftDropdown(tierIndex: number): void {
    if (this.giftDropdowns) {
      const dropdown = this.giftDropdowns.toArray()[tierIndex];
      if (dropdown) {
        dropdown.clearSelection();
      }
    }
  }

  // Validate minimum price is greater than previous tier
  validateTierMinimumPrice(tierIndex: number): string | null {
    const tiers = this.giftTiers$.value;
    if (tierIndex === 0) {
      return null; // First tier has no validation against previous
    }

    const currentTier = tiers[tierIndex];
    const previousTier = tiers[tierIndex - 1];

    const currentValue = currentTier.minimumCartPrice.value;
    const previousValue = previousTier.minimumCartPrice.value;

    if (currentValue !== undefined && previousValue !== undefined && currentValue <= previousValue) {
      return `Minimum price must be greater than previous tier (${previousValue})`;
    }

    return null;
  }

  // Check if the form is valid for saving
  isFormValid(): boolean {
    if (!this.configurationLoaded) return false;
    const generalValid = this.generalForm.valid;
    const giftValid = this.giftForm.valid;
    const freeShippingValid = this.freeShippingForm.valid;

    // If gift is enabled, validate tiers
    if (this.giftForm.controls.isGiftAllowed.value) {
      const tiers = this.giftTiers$.value;

      // Must have at least one tier
      if (tiers.length === 0) {
        return false;
      }

      // All tiers must be valid
      for (let i = 0; i < tiers.length; i++) {
        const tier = tiers[i];

        // Check minimum price is set and valid
        if (!tier.minimumCartPrice.valid) {
          return false;
        }

        // Check minimum price is greater than previous tier
        if (this.validateTierMinimumPrice(i)) {
          return false;
        }

        // Check at least one gift is selected
        if (tier.selectedGifts.value.length === 0) {
          return false;
        }
      }
    }

    return generalValid && giftValid && freeShippingValid;
  }

  save(e: ButtonClickEvent) {
    if (!this.configurationLoaded) return;
    const generalRules = this.generalForm.value;
    const giftRules = this.giftForm.value;
    const freeShippingRules = this.freeShippingForm.value;

    // Validate general rules
    if (!generalRules || generalRules.minimumCheckoutPrice === undefined || generalRules.minimumCheckoutPrice === null) {
      snackbar$.show('Please fill in the minimum checkout price', 'error');
      return;
    }

    // Validate gift rules if gift is enabled
    if (giftRules?.isGiftAllowed) {
      const tiers = this.giftTiers$.value;

      if (tiers.length === 0) {
        snackbar$.show('Please add at least one gift tier when gift is enabled', 'error');
        return;
      }

      // Validate each tier
      for (let i = 0; i < tiers.length; i++) {
        const tier = tiers[i];
        const minPrice = tier.minimumCartPrice.value;

        if (!minPrice || minPrice <= 0) {
          snackbar$.show(`Please fill in the minimum cart price for tier ${i + 1}`, 'error');
          return;
        }

        // Check that each tier's minimum price is greater than the previous
        const validationError = this.validateTierMinimumPrice(i);
        if (validationError) {
          snackbar$.show(`Tier ${i + 1}: ${validationError}`, 'error');
          return;
        }

        const selectedGiftIds = tier.selectedGifts.value.map(g => g.id);
        if (selectedGiftIds.length === 0) {
          snackbar$.show(`Please select at least one gift for tier ${i + 1}`, 'error');
          return;
        }
      }
    }

    // Validate free shipping rules if enabled
    if (freeShippingRules?.isFreeShippingEnabled) {
      if (!freeShippingRules.minimumCartPriceForFreeShipping || freeShippingRules.minimumCartPriceForFreeShipping <= 0) {
        snackbar$.show('Please fill in the minimum cart price for free shipping when free shipping is enabled', 'error');
        return;
      }
    }

    if (generalRules && giftRules && freeShippingRules) {
      // Build gift tiers array
      const giftTiers: GiftTier[] = this.giftTiers$.value.map(tier => ({
        minimumCartPrice: tier.minimumCartPrice.value || 0,
        products: tier.selectedGifts.value.map(g => g.id),
      }));

      const payload: CartRulesEntity = {
        minimumCheckoutPrice: generalRules.minimumCheckoutPrice,
        isGiftAllowed: giftRules.isGiftAllowed || false,
        tiers: giftTiers,
        isFreeShippingEnabled: freeShippingRules.isFreeShippingEnabled || false,
        minimumCartPriceForFreeShipping: freeShippingRules.minimumCartPriceForFreeShipping || 0,
        isTargetedFreeShippingEnabled: this.targetedShippingForm.controls.isTargetedFreeShippingEnabled.value ?? false,
        freeShippingCustomerIds: this.selectedCustomerIds,
        freeShippingProductIds: this.selectedProductIds,
      };

      this.cartRulesApi
        .set(payload)
        .pipe(e.pipe())
        .subscribe(() => {
          snackbar$.show('Rules Saved');
        });
    }
  }
}
